import { isChrome, isEdge, isIOS } from 'react-device-detect';
import { AudioContext, type IAudioContext } from 'standardized-audio-context';
import { type StateCreator } from 'zustand';
import { getSafeErrorFields } from '../errors';
import { pTimeout } from './pTimeout';
import { type AudioGraphStore } from './types';

export interface CoreSlice {
  isReady: boolean;
  audioContext: IAudioContext | null;
  init: () => void;
  start: (signal?: AbortSignal) => Promise<void>;
  cleanupDeviceListeners: () => void;
  teardownForLogout: () => void;
  rebuildGraph: (disconnectMicAnalyzer?: boolean, signal?: AbortSignal) => Promise<void>;
}

// Module-level references for side-effect cleanup.
// These live outside the store because they are DOM/timer handles, not state.
let deviceChangeListener: (() => void) | null = null;
let deviceCheckIntervalId: ReturnType<typeof setInterval> | null = null;

export const createCoreSlice: StateCreator<AudioGraphStore, [], [], CoreSlice> = (
  set,
  get
) => {
  return {
    audioContext: null,
    isReady: false,

    init: () => {
      const { addError, logError } = get();
      if (typeof window === 'undefined') return;

      try {
        // Can fail in some rare cases due to resources or compatibility issues.
        const audioContext = new AudioContext();

        set({
          audioContext,
          recorderDestinationNode: audioContext.createMediaStreamDestination(),
        });
      } catch (err) {
        addError('UNEXPECTED_ERROR');
        logError({ event_type: 'INIT_FAILURE', details: getSafeErrorFields(err) });
      }
    },

    start: async (signal) => {
      const { checkDevicesTask, loadDeviceFromStorage, testMic, logError } = get();
      set({ isPaused: false });

      await testMic();
      if (signal?.aborted) throw signal.reason;

      const audioContext = get().audioContext;
      if (!audioContext) {
        logError({ event_type: 'CONTEXT_RESUME_FAILED_NO_CONTEXT' });
        return;
      }

      try {
        await pTimeout(audioContext.resume(), 3000);
      } catch {
        logError({ event_type: 'CONTEXT_RESUME_TIMED_OUT' });
        return;
      }

      await loadDeviceFromStorage();
      if (signal?.aborted) throw signal.reason;

      // Tear down previous listeners/intervals before attaching new ones.
      // This prevents accumulation if start() is called multiple times.
      get().cleanupDeviceListeners();

      deviceChangeListener = () => void checkDevicesTask();
      navigator.mediaDevices.addEventListener('devicechange', deviceChangeListener);

      // Backup polling for browsers with unreliable devicechange events.
      if ((isChrome || isEdge) && !isIOS) {
        deviceCheckIntervalId = setInterval(() => void checkDevicesTask(), 5000);
      }

      // A fresh start is a new mic attempt, so stale DEVICE_IN_USE / INTERRUPTED
      // must clear: rebuildGraph's error gate skips acquisition without throwing,
      // which would flip isReady true with no source.
      get().clearError('DEVICE_IN_USE');
      get().clearError('INTERRUPTED');
      const disconnectMicAnalyzer = !!get().micAnalyzer;
      // Raw rebuildGraph, not rebuildGraphTask: start() already runs as the
      // queue's START task, and awaiting another task from inside one deadlocks
      // the single-lane queue.
      await get().rebuildGraph(disconnectMicAnalyzer, signal);
      if (signal?.aborted) throw signal.reason;
      // rebuildGraph resolves without acquiring when an uncleared error (e.g.
      // MIC_DISALLOWED, NO_DEVICES) gated it — only claim ready when the mic
      // source genuinely exists.
      if (!get().hasError()) set({ isReady: true });
    },

    cleanupDeviceListeners: () => {
      if (deviceChangeListener) {
        navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener);
        deviceChangeListener = null;
      }
      if (deviceCheckIntervalId) {
        clearInterval(deviceCheckIntervalId);
        deviceCheckIntervalId = null;
      }
    },

    teardownForLogout: () => {
      const queuedTasks = get().taskQueue;
      const cancellation = new Error('Audio graph shut down for logout');
      get().abortActiveTask(cancellation);
      get().cleanupDeviceListeners();
      get().unblockMic();
      try {
        get().audioLevelMonitor?.stop();
      } catch (err) {
        get().logError({
          event_type: 'LOGOUT_AUDIO_LEVEL_STOP_FAILED',
          details: getSafeErrorFields(err),
        });
      }
      get().cleanupMicSource();
      get().cleanupTabSource();
      const noDeviceTimeout = get().noDeviceTimeout;
      if (noDeviceTimeout) clearTimeout(noDeviceTimeout);
      set({
        isReady: false,
        listeners: [],
        micSourceStream: null,
        micSourceNode: null,
        tabSourceStream: null,
        tabSourceNode: null,
        videoStream: null,
        micAnalyzer: undefined,
        tabAnalyzer: undefined,
        micAnalyzerUsers: new Set(),
        tabAnalyzerUsers: new Set(),
        audioLevelMonitor: null,
        audioLevelMonitorUsers: new Set(),
        audioStatus: 'UNKNOWN',
        noDeviceTimeout: null,
        taskQueue: [],
        isPaused: true,
      });
      queuedTasks.forEach((task) => task.reject(cancellation));
    },

    rebuildGraph: async (disconnectMicAnalyzer?: boolean, signal?: AbortSignal) => {
      const { createMicSource, cleanupMicSource, hasError } = get();
      if (hasError()) return;
      cleanupMicSource(disconnectMicAnalyzer);
      await createMicSource(signal);
    },
  };
};
